import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductModel } from '@mango-engineering/maknet-client-sdk/lib/shop/product/model/product.model';
import { filter1pProducts } from '@mango-engineering/maknet-client-sdk/lib/shop/product/util/productUtils';
import { COUNTRY } from '@mango-engineering/maknet-client-sdk/lib/shop/types';
import { CarouselConfigs } from '../config/carouseConfig';
import { config } from '../config/env';
import { useDynamicData, useStaticData } from '../contexts/DataProvider.context';
import { getRecommendations } from '../services/getRecommendation.service';
import SendBrowserEvent from '../services/sendBrowserEvent.service';
import { CarouselImpressionEventProps } from '../types/analytics.types';
import {
  RecommendationType,
  CarouselSettingProps,
  TitleData,
  RecommendationPayload,
  RecommendationResponse,
  PlatformType,
} from '../types/common';
import { normalizeLocale } from '../types/locale.types';
import { Product } from '../types/product.types';
import { getFeatureMinVersion, isDiversifyRecommendationsEnabled, isFeatureEnabled } from '../utils/common.util';
import { AppMode, FALLBACK_APP_VERSION, PRODUCT_PAGE_SIZE, ProductCardVariant, TitleKey, WEBVIEW_MESSAGE_TYPE } from '../utils/constants';
import { getAnalyticsProducts, getBasketRecommendationTitle, getInjectedQueries, isFlagEnabledForCountry } from '../utils/helper';
import { mergeAfcStoreCodes } from '../utils/storeCodes';
import { useBasket } from './useBasket';
import useCampaignComplexPromotion from './useCampaignComplexPromotion';
import { useWebViewMessageListener } from './useWebViewMessageListener';

export const useCarouselHook = (searchParams: URLSearchParams) => {
  const { t } = useTranslation();
  const { getComplexPromotionV2 } = useCampaignComplexPromotion();
  const { refreshBasket } = useBasket();
  const { setUserDetails, userSegments, isHorecaMember, carouselModel, appMode, setCountryCode } = useStaticData();
  const { searchRequest, setIsExpressDelivery } = useDynamicData();
  const {
    setCurrentStoreCodes,
    setMmid,
    setAlcoholExpiryDate,
    setIsNative,
    setAppVersion,
    setExpressStoreCodes,
    afcStoreCodes,
    isAfcStoreEnabled,
  } = useStaticData();
  
  const {
    type,
    locale,
    // Short URL params to keep recommendation URL compact
    pId: productId,
    uid: userId,
    q: query,
    isExpressMode,
    sameDay,
    pv: productVariant,
    title,
    rv: recommendationVariant,
    expressStoreCodes,
    platform,
    brand: brandName,
    countryCode: countryCodeFromParams,
    mmid,
    alcoholExpiryDate,
    isNative,
    appVersion,
  } = Object.fromEntries(searchParams.entries());
  const activeLocale = normalizeLocale(locale);

  const [page, setPage] = useState(1);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isProductApiLoading, setIsProductApiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [carouselSettings, setCarouselSettings] = useState<CarouselSettingProps | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [storeCodesFromParent, setStoreCodesFromParent] = useState<string[]>();  
  const [accessTokenFromParent, setAccessTokenFromParent] = useState<string>();  
  const [shouldFetch, setShouldFetch] = useState(false);
  const isFetchingRef = useRef<string | null>(null);
  const isFetchDataRef = useRef<string | null>(null);
  const carouselMaxItemsCountFromConfig = config.SHOPPING_CAROUSEL_MAX_ITEM_COUNT_VIEW;
  const isPromoTagV2Config = config.LOYALTY_CAMPAIGN_PROMO_TAG_V2_ENABLED;
  const enableHorizonProductCard = config.ENABLE_HORIZON_PRODUCT_CARD;
  const diversifyRecommendationsConfig = config.ENABLE_DIVERSIFY_RECOMMENDATIONS;
  const alcoholProductAvailabilityConfig = config.ALCOHOL_PRODUCT_AVAILABILITY;
  const carouselMaxItem = carouselMaxItemsCountFromConfig ? JSON.parse(carouselMaxItemsCountFromConfig) : null;
  const expressMode = isExpressMode?.toLowerCase() === 'true';
  const isSameDay = sameDay?.toLowerCase() === 'true';
  const isFromNative = isNative?.toLowerCase() === 'true';
  // Initial WebView data is read once at mount.
// Late-arriving storeCodes/accessToken are handled through
// the STORE_CODES and ACCESS_TOKEN postMessage events below.
  const { storeCodes, accessToken: injectedAccessToken } = useMemo(() => getInjectedQueries(), []);
  const expressCodes = useMemo(() => (expressStoreCodes ? expressStoreCodes.split(',') : []), [expressStoreCodes]);
  const isUnifiedBasketEnabled = config.SHOPPING_ENABLE_UNIFIED_BASKET && isFromNative;
  const currentAppMode = useMemo(() => {
    return appMode ? (appMode as AppMode) : expressMode ? AppMode.express : AppMode.regular;
  }, [appMode, expressMode]);

  const isExpressDelivery = isUnifiedBasketEnabled ? Boolean(currentAppMode === AppMode.express) : expressMode;
  const countryCode = (countryCodeFromParams || COUNTRY.THAILAND) as COUNTRY;
  const horecaEnabledFromConfig = config.SHOPPING_ENABLE_HORECA;
  const horecaMinAppVersion = getFeatureMinVersion(countryCode, config.SHOPPING_ENABLE_HORECA_MIN_VERSION);
  const isHorecaEnabled = useMemo(
    () =>
      Boolean(
        horecaMinAppVersion &&
          isFlagEnabledForCountry(countryCode, horecaEnabledFromConfig) &&
          isFeatureEnabled(horecaMinAppVersion, appVersion || FALLBACK_APP_VERSION),
      ),
    [appVersion, countryCode, horecaEnabledFromConfig, horecaMinAppVersion],
  );
  const isHorecaMemberEnabled = Boolean(isHorecaEnabled && isHorecaMember);
  const horizonUiUxMinVersion = getFeatureMinVersion(countryCode, config.HORIZON_UI_UX_MIN_VERSION);
  const hasAlcoholProductAvailability = useMemo(
    () => isFlagEnabledForCountry(countryCode, alcoholProductAvailabilityConfig),
    [alcoholProductAvailabilityConfig, countryCode],
  );

  const isHorizonProductCardEnabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, enableHorizonProductCard) && isFromNative);
  }, [countryCode, enableHorizonProductCard, isFromNative]);

  const isHorizonUiUxEnabled = useMemo(() => {
    return isHorizonProductCardEnabled && isFeatureEnabled(horizonUiUxMinVersion, appVersion || FALLBACK_APP_VERSION);
  }, [appVersion, horizonUiUxMinVersion, isHorizonProductCardEnabled]);

  const enableVerticalPagination = isHorizonUiUxEnabled;

  const isPromoTagV2Enabled = useMemo(() => {
    return Boolean(isFlagEnabledForCountry(countryCode, isPromoTagV2Config) && isFromNative && isHorizonUiUxEnabled);
  }, [countryCode, isPromoTagV2Config, isFromNative, isHorizonUiUxEnabled]);

  const isClubMemberPriceEnabled = config.CLUB_MEMBER_PRICE_ENABLED;

  const isDiversify = useMemo(() => {
    return isDiversifyRecommendationsEnabled(diversifyRecommendationsConfig, type ?? '');
  }, [diversifyRecommendationsConfig, type]);

  const resetKey = useMemo(
    () =>
      JSON.stringify({
        searchRequest,
        type,
        currentAppMode,
        recommendationVariant,
        countryCode,
        productId,
        locale: activeLocale,
        afcStoreCodes,
        isAfcStoreEnabled,
        hasAlcoholProductAvailability,
      }),
    [
      searchRequest,
      type,
      currentAppMode,
      recommendationVariant,
      countryCode,
      productId,
      activeLocale,
      afcStoreCodes,
      isAfcStoreEnabled,
      hasAlcoholProductAvailability,
    ],
  );
  
  const handleMessage = useCallback((event: MessageEvent) => {
    if (event.data?.type === WEBVIEW_MESSAGE_TYPE.STORE_CODES) {
      const storeCodes = event.data?.message?.storeCodes;
      const hasValidStoreCodes =
        Array.isArray(storeCodes) && storeCodes.every((code): code is string => typeof code === 'string');

      if (hasValidStoreCodes) {
        // Bug fix (iframe loop guard): only update when the store-codes payload
        // actually differs from what we already hold. Without this guard, parents
        // that re-broadcast STORE_CODES on every state change of their own (or
        // a StrictMode double-invoke of IFRAME_READY) cause the carousel to
        // schedule a state update every time → setCurrentStoreCodes →
        // staticValue identity change → fetch effect re-fires → another
        // response from the parent → loop.
        setStoreCodesFromParent((prev) =>
          prev && prev.length === storeCodes.length && prev.every((c, i) => c === storeCodes[i])
            ? prev
            : storeCodes,
        );
      }
    }

    if (event.data?.type === WEBVIEW_MESSAGE_TYPE.ACCESS_TOKEN) {
      const accessToken = event.data?.message;

      if (typeof accessToken === 'string') {
        // Same guard for access token: parents occasionally re-broadcast the
        // token; treat identical payloads as a no-op.
        setAccessTokenFromParent((prev) => (prev === accessToken ? prev : accessToken));
      }
    }
  }, []);

  useWebViewMessageListener(handleMessage);


  const currentStoreCodes = useMemo(() => {
    return storeCodesFromParent?.length
      ? storeCodesFromParent
      : storeCodes;
  }, [storeCodesFromParent, storeCodes]);

  const recommendationStoreCodes = useMemo(
    () => mergeAfcStoreCodes(currentStoreCodes, afcStoreCodes, isAfcStoreEnabled),
    [afcStoreCodes, currentStoreCodes, isAfcStoreEnabled],
  );

  const accessToken = useMemo(() => {
    return accessTokenFromParent
      ? accessTokenFromParent
      : injectedAccessToken;
  }, [accessTokenFromParent, injectedAccessToken]);

  useEffect(() => {
    setProducts([]);
    setCarouselSettings(null);
    setPage(1);
    setShouldFetch(true);
  }, [resetKey]);

  useEffect(() => {
    setMmid(mmid);
    setIsExpressDelivery(isExpressDelivery);
    setCurrentStoreCodes(currentStoreCodes);
    setExpressStoreCodes(expressCodes);
    setAlcoholExpiryDate(alcoholExpiryDate);
    setIsNative(isFromNative);
    setAppVersion(appVersion);
  }, [
    mmid,
    setMmid,
    isExpressDelivery,
    setIsExpressDelivery,
    currentStoreCodes,
    setCurrentStoreCodes,
    alcoholExpiryDate,
    setAlcoholExpiryDate,
    isFromNative,
    setIsNative,
    appVersion,
    setAppVersion,
    expressCodes,
    setExpressStoreCodes,
  ]);

  const requestParams = useMemo<RecommendationPayload>(
    () => ({
      page: page,
      type: type as RecommendationType,
      productId,
      isExpressMode: expressMode,
      storeCodes: currentStoreCodes,
      recommendationStoreCodes,
      afcStoreCodes,
      isAfcStoreEnabled,
      hasAlcoholProductAvailability,
      accessToken: accessToken ?? '',
      userId,
      userSegments,
      isHorecaMemberEnabled,
      diversifyResults: isDiversify,
      recommendationVariant: recommendationVariant ?? '',
      isSameDay,
      appMode: currentAppMode,
      ...(searchRequest && {
        searchRequest: searchRequest,
      }),
      countryCode,
      locale: activeLocale,
    }),
    [
      type,
      productId,
      expressMode,
      currentStoreCodes,
      recommendationStoreCodes,
      afcStoreCodes,
      isAfcStoreEnabled,
      hasAlcoholProductAvailability,
      searchRequest,
      accessToken,
      userId,
      userSegments,
      isHorecaMemberEnabled,
      isDiversify,
      recommendationVariant,
      isSameDay,
      currentAppMode,
      countryCode,
      activeLocale,
      page,
    ],
  );

  const getTitle = useCallback(
    (titleKey: string, brandName?: string): TitleData => {
      const translatedTitle = t(`carousel.title.${titleKey}`, {
        lng: activeLocale,
      });
      const text =
        titleKey === TitleKey.SIMILAR_PRODUCTS && brandName ? `${translatedTitle} ${brandName}` : translatedTitle;

      return {
        text,
        icon: false,
      };
    },
    [t, activeLocale],
  );

  const triggerCarouseImpressionAnalytic = useCallback(
    (result: RecommendationResponse, carouselName: string) => {
      const analyticsProps: CarouselImpressionEventProps = {
        products: getAnalyticsProducts(result?.products ?? []),
        query: query ?? '',
        carouselName: (carouselModel?.analyticsName ?? carouselName) as string,
        carouselIndex: (carouselModel?.carouselIndex || carouselSettings?.carouselIndex || 0) as number,
        positionOnPage: (carouselModel?.carouselIndex || 0) as number,
        fixed: carouselModel?.fixed as boolean,
        boostedScore: (carouselModel?.boostedScore || 0) as number,
        getPersonalizedRecommendationsFromDb: result?.recommendationType ?? '',
        algorithm: result?.algorithm,
        position: (carouselModel?.position || 0) as number,
        personalised: result?.personalised,
        renderSize: (productVariant as ProductCardVariant) ?? ProductCardVariant.NORMAL,
      };

      SendBrowserEvent.triggerCarouselImpressionEvent(
        analyticsProps,
        type === RecommendationType.BASKET_RECOMMENDATION,
      );
    },
    [query, carouselModel, carouselSettings?.carouselIndex, productVariant, type],
  );

  const fetchRecommendation = useCallback(
    async (requestParams: RecommendationPayload) => {
      const fetchKey = JSON.stringify({
        type: requestParams.type,
        page: requestParams.page,
        productId: requestParams.productId,
        locale: requestParams.locale,
        storeCodes: requestParams.storeCodes,
        recommendationStoreCodes: requestParams.recommendationStoreCodes,
        afcStoreCodes: requestParams.afcStoreCodes,
        isAfcStoreEnabled: requestParams.isAfcStoreEnabled,
        hasAlcoholProductAvailability: requestParams.hasAlcoholProductAvailability,
        userSegments: requestParams.userSegments,
        isHorecaMemberEnabled: requestParams.isHorecaMemberEnabled,
      });

      if (isFetchingRef.current === fetchKey) return;

      isFetchingRef.current = fetchKey;

      const isFirstPage = requestParams.page === 1;

      if (isFirstPage) {
        setIsInitialLoading(true);
      } else {
        setIsPaginationLoading(true);
      }

      const configData = { ...CarouselConfigs[type] };

      try {
        const payload = { ...requestParams };

        if (isUnifiedBasketEnabled && expressCodes?.length > 0) {
          payload.expressStoreCodes = expressCodes;

          if (isExpressDelivery) {
            payload.storeCodes = expressCodes;
            payload.recommendationStoreCodes = mergeAfcStoreCodes(
              expressCodes,
              payload.afcStoreCodes,
              payload.isAfcStoreEnabled,
            );
          }
        }

        const result = await getRecommendations(
          payload,
          setIsInitialLoading,
          setIsProductApiLoading,
          undefined,
          isPromoTagV2Enabled,
          isClubMemberPriceEnabled ? mmid : undefined,
          isFromNative,
        );

        // LAST PAGE
        if (!result?.products?.length) {
          setShouldFetch(false);

          // only hide carousel if first page itself is empty
          if (requestParams.page === 1) {
            SendBrowserEvent.sendProductCount(result?.products?.length);
            setCarouselSettings(null);
            SendBrowserEvent.sendRecommendations(result, type === RecommendationType.BASKET_RECOMMENDATION);
          }

          return;
        }

        const mergedProducts = [...(requestParams.page === 1 ? [] : products), ...result.products].filter(
          (product, index, self) => index === self.findIndex((p) => p.id === product.id),
        );

        setProducts(mergedProducts);

        let brandNameFromSubProduct = '';

        if (result.products.length > 0) {
          brandNameFromSubProduct = result.products[0]?.brand ?? '';
        }

        const updatedTitleData = getTitle(configData.titleKey, brandName ?? brandNameFromSubProduct);
        const newTitle = title && title !== 'undefined' ? title : (updatedTitleData.text ?? '');

        const carouselSettingsData = {
          ...configData,
          data: {
            ...result,
            products: mergedProducts,
          },
          productCardVariant:
            (productVariant as ProductCardVariant) ??
            (type === RecommendationType.SEARCH_START_TOP_PICKS
              ? ProductCardVariant.COMPACT
              : ProductCardVariant.NORMAL),
          recommendationType: type,
          countryCode,
          ...(carouselMaxItem &&
            Object.entries(carouselMaxItem)?.length && {
              carouselMaxItemsCount: carouselMaxItem,
            }),
          isDiversify: isDiversify,
          isVerticalScroll: enableVerticalPagination,
          title:
            recommendationVariant === RecommendationType.BASKET_RECOMMENDATION
              ? t(getBasketRecommendationTitle(result?.recommendationType, isHorizonUiUxEnabled))
              : newTitle,
          titleIcon:
            type === RecommendationType.SEARCH_START_TOP_PICKS || type === RecommendationType.BASKET_RECOMMENDATION,
          ...(platform && { platform: platform as PlatformType }),
        };

        setCarouselSettings(carouselSettingsData);

        // Bug fix (vertical pagination): only signal "fetch more" when there are
        // actually more items to load. Previously we always set `true` here,
        // which — combined with the auto-fetch in the vertical-scroll carousel
        // (`enableVerticalPagination`) — caused an extra page-3 call returning
        // an empty array whenever the last page was partial (e.g. 25 items →
        // page 1: 20, page 2: 5, page 3: []).
        //
        // When `totalCount` is reported by the API we use it as the source of
        // truth. Some endpoints don't return `totalCount`; fall back to "more
        // pages may exist if this page was full" in that case, which matches
        // the previous behaviour for those endpoints.
        const totalCount = result?.totalCount;
        const currentPage = requestParams.page ?? 1;
        const itemsLoadedAfterThisPage = currentPage * PRODUCT_PAGE_SIZE;
        const hasMoreFromTotal =                                                                                                                                                                                                      
          typeof totalCount === 'number'                                                                                                                                                                                              
            ? totalCount > itemsLoadedAfterThisPage                                                                                                                                                                                 
            : true; // unknown total: preserve legacy "try one more page" behaviour```
        const hasMoreFromPageSize =
          typeof totalCount !== 'number' && result.products.length >= PRODUCT_PAGE_SIZE;
        setShouldFetch(hasMoreFromTotal || hasMoreFromPageSize);

        SendBrowserEvent.sendRecommendations(result, type === RecommendationType.BASKET_RECOMMENDATION);

        triggerCarouseImpressionAnalytic(result, title ?? updatedTitleData.text);

        SendBrowserEvent.carouselNotEmpty(type, type === RecommendationType.BASKET_RECOMMENDATION);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'An unexpected error occurred');
      } finally {
        isFetchingRef.current = null;
        setIsInitialLoading(false);
        setIsPaginationLoading(false);
        setIsProductApiLoading(false);
      }
    },
    [
      type,
      productVariant,
      carouselMaxItem,
      expressCodes,
      isUnifiedBasketEnabled,
      isDiversify,
      title,
      platform,
      getTitle,
      triggerCarouseImpressionAnalytic,
      isHorizonUiUxEnabled,
      brandName,
      recommendationVariant,
      isExpressDelivery,
      countryCode,
      enableVerticalPagination,
      t,
      isPromoTagV2Enabled,
      mmid,
      isClubMemberPriceEnabled,
      isFromNative,
      products,
    ],
  );

  useEffect(() => {
    if (countryCode) {
      setCountryCode(countryCode);
    }
  }, [countryCode, setCountryCode, refreshBasket]);

  useEffect(() => {
    const fetchData = async () => {
      if (currentStoreCodes.length === 0) return;

      const fetchKey = JSON.stringify({ ...requestParams, storeCodes: requestParams.storeCodes });
      if (isFetchDataRef.current === fetchKey) return;
      isFetchDataRef.current = fetchKey;
      let params: RecommendationPayload = {
        ...requestParams,
        page,
      };

      if ((type as RecommendationType) === RecommendationType.ADSCAROUSEL && searchRequest?.q?.length !== 0) {
        params = {
          ...params,
          searchRequest: {
            includeCurations: true,
            ...searchRequest,
          },
        };
      }

      await fetchRecommendation(params);
    };

    fetchData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    type,
    recommendationVariant,
    currentAppMode,
    searchRequest,
    accessToken,
    currentStoreCodes,
    activeLocale,
    afcStoreCodes,
    isAfcStoreEnabled,
    hasAlcoholProductAvailability,
    userSegments,
    isHorecaMemberEnabled,
  ]);

  useEffect(() => {
    if (!accessToken && !userId) return; // do not actively clear
    if (accessToken || userId) {
      setUserDetails?.((previous) => ({
        ...previous,
        token: accessToken ?? '',
        userId: userId ?? '',
        isExpressMode: isExpressMode?.toLowerCase() === 'true',
      }));
    }
  }, [accessToken, userId, isExpressMode, setUserDetails]);

  useEffect(() => {
    if (!products || currentStoreCodes.length === 0) return;

    const id = setTimeout(() => {
      const filtered1pSearchProducts = filter1pProducts(products as unknown as ProductModel[])?.filter(
        (p) => p?.id && p?.displayPrice !== undefined,
      );

      const productIds = filtered1pSearchProducts.map((product) => ({
        shopifyProductId: product.id!,
        price: product.displayPrice!.toString(),
      }));

      if (productIds.length > 0) {
        getComplexPromotionV2(productIds, currentStoreCodes, filtered1pSearchProducts as unknown as Product[]);
      }
    }, 0);

    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, currentStoreCodes]);

  const fetchMoreRecommendation = useCallback(() => {
    if (isFetchingRef.current || isPaginationLoading || !shouldFetch) {
      return;
    }

    setPage((prev) => prev + 1);
  }, [isPaginationLoading, shouldFetch]);

  return {
    carouselSettings,
    isProductApiLoading,
    isInitialLoading,
    isPaginationLoading,
    error,
    fetchMoreRecommendation,
    shouldFetch,
  };
};
